// Hand-written mirror of `contract/src/atlas_contract/*.py`. Keep in sync
// with the Python source; Phase K's drift test will catch divergence.

export type PipelineId =
  | "reverse_image"
  | "ai_gemini"
  | "ai_claude"
  | "ai_gpt"
  | "ocr"
  | "meta"
  | "deep_mode"
  | "minimap";

export type PipelineStatus =
  | "running"
  | "complete"
  | "failed"
  | "cancelled"
  | "skipped";

export type PipelineErrorCategory =
  | "rate_limit"
  | "timeout"
  | "network"
  | "auth"
  | "validation"
  | "internal";

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Guess {
  lat: number;
  lng: number;
  confidence: number;
  label: string;
  uncertainty_km: number | null;
  source: string;
  reasoning: string | null;
}

export interface PipelineError {
  category: PipelineErrorCategory;
  message: string;
  detail: Record<string, unknown>;
}

export interface DetectedSignal {
  type: string;
  value: unknown;
  confidence: number;
  explanation: string | null;
}

export interface PipelineResult {
  pipeline: PipelineId;
  status: PipelineStatus;
  started_at_ms: number;
  completed_at_ms: number | null;
  latency_ms: number | null;
  guesses: Guess[];
  detected_signals: DetectedSignal[] | null;
  error: PipelineError | null;
  model_id: string | null;
}

export interface PipelineStartedEvent {
  id: string;
  pipeline: PipelineId;
  started_at_ms: number;
}

export interface PipelineResultEvent {
  id: string;
  pipeline: PipelineId;
  status: PipelineStatus;
  started_at_ms: number;
  completed_at_ms: number;
  latency_ms: number;
  error_category: PipelineErrorCategory | null;
  error_message: string | null;
  /// Per-model pinpoint payload. Present on `complete` for the AI
  /// pipelines (ai_gemini, ai_claude, ai_gpt); absent for non-LLM
  /// pipelines and for failed/cancelled outcomes. Carries the model's
  /// own coordinate guess and reasoning, separate from the synthesized
  /// final guess that lives on `state://final_guess`.
  guess?: LlmGuessPayload;
  /// OCR payload — present on `complete` for the `ocr` pipeline.
  /// Carries extracted text + place-name candidate.
  ocr?: OcrPayload;
  /// Meta-detector payload — present on `complete` for the `meta`
  /// pipeline. Lists region-narrowing signals + an overall region
  /// guess from the model.
  meta?: MetaPayload;
}

export interface OcrPayload {
  place_name: string | null;
  country: string | null;
  confidence: number;
  extracted: { text: string; kind: string }[];
}

export interface MetaPayload {
  region_guess: string;
  confidence: number;
  signals: {
    type: string;
    value: string;
    confidence: number;
    implies: string;
  }[];
}

export interface LlmGuessPayload {
  lat: number;
  lng: number;
  confidence: number;
  region: string;
  reasoning: string;
}

export interface FinalGuessEvent {
  lat: number;
  lng: number;
  label: string;
  source: string;
  confidence: number;
}

export interface CaptureState {
  last_capture_id: string | null;
  last_thumbnail_path: string | null;
  round_number: number;
  captured_at_ms: number | null;
  seconds_since_capture: number | null;
}

export interface FirstRunStatus {
  openrouter_configured: boolean;
  r2_configured: boolean;
  geonames_configured: boolean;
}

export interface FinalGuess {
  lat: number;
  lng: number;
  label: string;
  source: string;
  /// Aggregate confidence (max across contributing model confidences).
  /// Optional for backwards compatibility with the pre-ensemble shape.
  confidence?: number;
}

/// Closed set of GeoGuessr modes the LLM ensemble specialises for.
/// Mirrors atlas_core::config::GameMode (snake_case serialisation).
/// The `unknown` variant is the conservative fallback when auto-
/// detection can't classify the screen.
export type GameMode =
  | "classic_location"
  | "classic_country"
  | "country_streak"
  | "daily_challenge"
  | "duels"
  | "team_duels"
  | "battle_royale_distance"
  | "battle_royale_countries"
  | "live_challenge"
  | "unknown";

/// Two output classes the modes collapse into. Drives whether the
/// FinalGuessFooter displays lat/lng + Leaflet map (precise location)
/// or just the country name (country-only modes).
export type OutputClass = "precise_location" | "country_only";

export const COUNTRY_ONLY_MODES: ReadonlySet<GameMode> = new Set<GameMode>([
  "classic_country",
  "country_streak",
  "battle_royale_countries",
]);

export function outputClassFor(mode: GameMode): OutputClass {
  return COUNTRY_ONLY_MODES.has(mode) ? "country_only" : "precise_location";
}

/// Human-readable labels for the dropdown. Order matters — most
/// common modes first.
export const GAME_MODE_LABELS: Record<GameMode, string> = {
  classic_location: "Classic — Location",
  duels: "Duels (1v1)",
  team_duels: "Team Duels (2v2 / 3v3)",
  daily_challenge: "Daily Challenge",
  classic_country: "Classic — Country",
  country_streak: "Country Streak",
  battle_royale_distance: "Battle Royale (Distance)",
  battle_royale_countries: "Battle Royale (Countries)",
  live_challenge: "Live Challenge / Custom",
  unknown: "Unknown",
};

export const GAME_MODE_ORDER: readonly GameMode[] = [
  "classic_location",
  "duels",
  "team_duels",
  "daily_challenge",
  "classic_country",
  "country_streak",
  "battle_royale_distance",
  "battle_royale_countries",
  "live_challenge",
  "unknown",
];
